import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../Contexts/hooks';
import { useNotification } from '../Contexts/NotificationContext';
import { performEnterpriseGPTApiCall } from './useMiddlewareApiCall';
import { removeConversationAction, updateConversationAction } from '../store/actions/conversationActions';
import { setUserPreferencesAction } from '../store/actions/userPreferencesAction';
import { useConversations, useUserPreferences } from '../store/hooks';
import { AppConversation } from '../store/models/conversation';
import { ActionTypes, ConversationPatch, GptTypes, IUserPreferences, UserPreferences } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { buildRoute, ChatRoute, ChatSpecificRoute } from '../Routes';

export const useConversationActions = () => {
    const { chats, activeChat, dispatch } = useConversations();
    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();
    const { t } = useTranslation();
    const confirm = useConfirm();
    const { dispatchNotification } = useNotification();
    const navigate = useNavigate();

    const resolveFallbackConversation = (items: AppConversation[]) => {
        return items.find((c) => c.draft) ||
               items.find((c) => c.gpt?.type === GptTypes.SyGpt && (!c.prompts?.length)) ||
               items.find((c) => !c.prompts?.length) ||
               items.find((c) => c.gpt?.type === GptTypes.SyGpt) ||
               items[0];
    };

    const handleRemoveConversation = (id: string) => {
        confirm({
            message: '',
            show: true,
            title: t('menu.conversations.actions.remove.confirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    const isDeletingActiveConversation = activeChat?.id === id;
                    const remainingChats = chats.filter((conversation) => conversation.id !== id);
                    const fallbackConversation = isDeletingActiveConversation ? resolveFallbackConversation(remainingChats) : undefined;

                    await client.deleteConversation(id, cancelToken);
                    dispatch(removeConversationAction(id));

                    if (isDeletingActiveConversation) {
                        if (fallbackConversation) {
                            navigate(buildRoute(ChatSpecificRoute, { id: fallbackConversation.id }));
                        } else {
                            navigate(ChatRoute);
                        }
                    }
                } catch {
                    dispatchNotification('error.api.delete.conversation', 'error');
                }
            });
        });
    };

    const handleConversationTitleChange = (id: string, title: string) => {
        const patch = new ConversationPatch({
            ...chats.find((c) => c.id === id),
            title,
            actionType: ActionTypes.RenameConversation
        });

        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const patched = await client.patchConversation(id, patch, cancelToken);
                dispatch(updateConversationAction({ id: patched.id, patch }));
            } catch {
                dispatchNotification('error.api.patch.conversation', 'error');
            }
        });
    };

    const handlePinToggle = (id: string, pinned: boolean) => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                const existing: string[] = (userPreferences as IUserPreferences)?.pinnedConversations ?? [];
                let updatedPinned: string[];
                if (pinned) {
                    updatedPinned = Array.from(new Set([...(existing ?? []), id]));
                } else {
                    updatedPinned = (existing ?? []).filter(x => x !== id);
                }

                const updatedUserPreferences: IUserPreferences = {
                    ...(userPreferences as IUserPreferences),
                    pinnedConversations: updatedPinned
                };

                userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
            } catch {
                dispatchNotification('error.api.patch.userPreferences', 'error');
            }
        });
    };

    return {
        handleRemoveConversation,
        handleConversationTitleChange,
        handlePinToggle,
    };
};
